import { Link, useOutletContext } from 'react-router-dom';
import { useChanges, useClientRole, useInvoices, useRenew } from '@/api';
import { ConfirmButton } from '@/components/ConfirmDialog';
import { Avatar, Meta, Status } from '@/components/ui';
import { clientCan, OWNER_DECIDES } from '@/lib/access';
import { nextPeriodOf, periodLabel } from '@/lib/billing';
import { useContour } from '@/lib/contour';
import { formatDate, formatDue, formatMoney } from '@/lib/format';
import { invoiceStatus } from '@/lib/status';
import type { ProjectContext } from './ProjectShell';

/** The server renews by 91 days (a quarter); the date shown in the question is the same one. */
const renewedUntil = (until: string) => new Date(new Date(until).getTime() + 91 * 86_400_000).toISOString().slice(0, 10);

/** Money and paperwork of a project live here, one level down from «what's happening now». */
export function ProjectTerms() {
  const { project: p } = useOutletContext<ProjectContext>();
  const role = useClientRole();
  const money = clientCan(role, 'money');
  const { data: invoices } = useInvoices(money);
  const mine = invoices?.filter((i) => i.projectId === p.id) ?? [];
  const { data: changes } = useChanges(p.id);
  const renew = useRenew();
  const contour = useContour();
  const until = p.kind === 'retainer' ? p.terms.until : undefined;
  const soon = until ? new Date(until).getTime() - Date.now() < 15 * 86_400_000 : false;
  const paid = mine.filter((i) => i.status === 'paid').reduce((n, i) => n + i.paid.amountMinor, 0);

  return (
    <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
      <section>
        <h2 className="mb-2 text-sm font-semibold">Условия</h2>
        <Meta
          items={[
            { label: 'Формат', value: p.kind === 'fixed' ? 'Фиксированная стоимость за результат' : 'Регулярные ежемесячные платежи' },
            { label: 'Стоимость', value: <span className="num">{formatMoney(p.terms.price)}{p.kind === 'retainer' ? ' в месяц' : ' за проект'}</span> },
            ...(p.kind === 'fixed'
              ? [
                  ...(money ? [{ label: 'Оплачено', value: <span className="num">{formatMoney({ amountMinor: paid, currency: p.terms.price.currency })} из {formatMoney(p.terms.price)}</span> }] : []),
                  { label: 'Сдача', value: formatDate(p.finishBy) },
                ]
              : [{ label: 'Следующий счёт', value: `${formatDate(p.terms.nextInvoiceAt)}, за ${periodLabel(nextPeriodOf(p.terms))}${p.terms.billingCovers === 'next' ? ' (предоплата)' : ''}` }]),
            ...(p.terms.contract || contour?.legal.basis ? [{ label: 'Основание', value: p.terms.contract ?? contour?.legal.basis }] : []),
            { label: 'С нами с', value: formatDate(p.terms.since) },
          ]}
        />
        {until && (
          <div className="mt-6 rounded-lg p-4 shadow-hair">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium">Регулярные платежи действуют до {formatDate(until)}</span>
              <span className={soon ? 'text-sm text-waiting' : 'text-sm text-muted'}>{formatDue(until)}</span>
            </div>
            {soon && (
              <>
                <p className="mt-1 text-sm text-muted">Продлим на три месяца на тех же условиях. Если хотите что-то поменять в объёме — напишите в «Идеи и запросы».</p>
                {clientCan(role, 'admin') ? (
                  <ConfirmButton
                    label="Продлить на 3 месяца"
                    className="btn-primary mt-3"
                    disabled={renew.isPending}
                    title="Продлить регулярные платежи?"
                    confirmLabel="Продлить на 3 месяца"
                    onConfirm={() => renew.mutate(p.id)}
                  >
                    Регулярные платежи по «{p.title}» продлятся на три месяца, до {formatDate(renewedUntil(until))}, на тех же условиях ({formatMoney(p.terms.price)} в месяц). Счета будут выставляться и дальше.
                  </ConfirmButton>
                ) : (
                  role && <p className="mt-3 text-sm text-muted">{OWNER_DECIDES}</p>
                )}
                {renew.error && <p className="mt-2 text-sm text-overdue" role="alert">{(renew.error as Error).message}</p>}
              </>
            )}
          </div>
        )}
        {!!changes?.length && (
          <p className="mt-6 text-sm">
            Запросов на изменение: {changes.length}.{' '}
            <Link to={`/p/${p.id}/changes`} className="underline underline-offset-2">
              История и оценки
            </Link>
          </p>
        )}
      </section>

      <section>
        {money && (
          <>
        <h2 className="mb-2 text-sm font-semibold">Счета по проекту</h2>
        {mine.length ? (
          <ul className="divide-y divide-line border-y border-line">
            {mine.map((i) => {
              const s = invoiceStatus[i.status];
              return (
                <li key={i.id}>
                  <Link to={`/money/${i.id}`} className="flex items-center gap-4 py-3 hover:bg-panel">
                    <span className="flex-1 text-sm">
                      Счёт № {i.number} <span className="text-muted">от {formatDate(i.issuedAt)}</span>
                    </span>
                    <span className="num text-sm">{formatMoney(i.total)}</span>
                    <Status tone={s.tone} className="w-36 justify-end">{s.label}</Status>
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted">Счетов пока не было.</p>
        )}
          </>
        )}

        <h2 className={money ? 'mb-3 mt-10 text-sm font-semibold' : 'mb-3 text-sm font-semibold'}>Команда проекта</h2>
        <ul className="space-y-3">
          {p.team.map((m) => (
            <li key={m.id} className="flex items-center gap-3">
              <Avatar person={m} />
              <div className="leading-tight">
                <div className="text-sm font-medium">{m.name}</div>
                <div className="text-xs text-muted">{m.role}{m.id === p.owner.id ? ', ведёт проект' : ''}</div>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
